// The deeper numbers (SCENARIOS E8, PLAN-2026-10-03-research-tools.md §11,
// B-256) in words and figures: Players' columns and a phone's line, and the
// card's section, at the Everything level.
//
// NUMBERS WITH THEIR NAMES, never a verdict: *"+3.2"* is form, never *hot*;
// a range is two weeks, never *boom* or *bust*
// (docs/decisions/2026-09-12-tools-not-instructions.md). No average over the
// rest of the season's schedule since 2026-10-26 (B-519): a week's matchup is
// the row's and the card's own.

import type { DeeperNumbers } from '@fsp/domain';
import { useActiveLeague } from '../../data/LeaguesProvider';
import { Caption, Stack, Text } from '../../kit/ui';
import { getSport } from '../../sports/registry';

/** The stats usage is measured in, named as the sport's catalog names them. */
export interface UsageLabels {
  share: { name: string; shortName: string };
  perGame: { name: string; shortName: string } | null;
}

/**
 * The active league's sport's usage stats (`SportDefinition.usage`), or null
 * for a sport that measures none — the words the numbers are labeled with,
 * from the contract, never typed here.
 */
export function useUsageLabels(): UsageLabels | null {
  const sport = useActiveLeague()?.league.sport;
  if (sport === undefined) return null;
  let definition;
  try {
    definition = getSport(sport as never);
  } catch {
    return null;
  }
  const usage = definition.usage;
  if (usage === undefined) return null;
  const named = (key: string) => {
    const stat = definition.stats.find((s) => s.key === key);
    return { name: stat?.name ?? key, shortName: stat?.shortName ?? key };
  };
  return {
    share: named(usage.share),
    perGame: usage.perGame === undefined ? null : named(usage.perGame),
  };
}

/** "24%". */
export function shareText(share: number | null | undefined): string {
  return share === null || share === undefined ? '—' : `${Math.round(share * 100)}%`;
}

// NO "HIS" IN THESE WORDS (B-515): they are said of a team's defense too,
// which the card called "his" throughout.

/** "of the club's targets over 8 games, 31% over the last three": the share's words, after it. */
export function shareTail(usage: NonNullable<DeeperNumbers['usage']>, labels: UsageLabels): string {
  return (
    `of the club's ${labels.share.name.toLowerCase()} over ${usage.games} ` +
    `${usage.games === 1 ? 'game' : 'games'}` +
    // The last three only once there are more games than three.
    (usage.games > 3 ? `, ${shareText(usage.shareLastThree)} over the last three` : '')
  );
}

/** "24% of the club's targets over 8 games, 31% over the last three". */
export function shareWords(usage: DeeperNumbers['usage'], labels: UsageLabels): string {
  if (usage === null) return `No ${labels.share.name.toLowerCase()} measured this season`;
  return `${shareText(usage.share)} ${shareTail(usage, labels)}`;
}

/** "+3.2", "−1.4", "0.0" (anything that rounds to it); a dash with nothing to measure. */
export function formText(form: number | null): string {
  if (form === null) return '—';
  if (Math.abs(form) < 0.05) return '0.0';
  return form > 0 ? `+${form.toFixed(1)}` : `−${Math.abs(form).toFixed(1)}`;
}

export function formWords(form: number | null, recent: readonly number[] = []): string {
  // HIS LAST GAMES IN WORDS (B-410): the line beside the figure is a picture
  // of these, and a picture is never the only place a number lives.
  const games =
    recent.length < 2
      ? ''
      : `. The last ${recent.length} games, oldest first: ${recent.map((p) => p.toFixed(1)).join(', ')}`;
  return form === null
    ? `Form: not enough weeks yet${games}`
    : `Form ${formText(form)}: points a game over the last three weeks, less over the season${games}`;
}

/** "4.0–25.0". */
/**
 * "3.8–36.8", or "4–37" in a tablet's column, whose width cut the tenths off
 * as "23.3–4…"; the tenths are in the words a rest or a tap brings.
 */
export function rangeText(range: DeeperNumbers['range'], whole = false): string {
  if (range === null) return '—';
  const n = (v: number) => (whole ? String(Math.round(v)) : v.toFixed(1));
  return `${n(range.low)}–${n(range.high)}`;
}

export function rangeWords(range: DeeperNumbers['range']): string {
  if (range === null) return 'Range: no weeks yet';
  return (
    `Lowest week ${range.low.toFixed(1)}, highest ${range.high.toFixed(1)}; ` +
    `${range.twiceAverage} of ${range.weeks} ${range.weeks === 1 ? 'week' : 'weeks'} at twice the average`
  );
}

/** A phone's one line: "Form +3.2 · 4.0–25.0 · Tgt 24%"; null with nothing to say. */
export function deeperLine(d: DeeperNumbers, labels: UsageLabels | null): string | null {
  const parts = [
    d.form === null ? null : `Form ${formText(d.form)}`,
    d.range === null ? null : rangeText(d.range),
    d.usage === null || labels === null
      ? null
      : `${labels.share.shortName} ${shareText(d.usage.share)}`,
    d.usage === null || d.usage.perGame === null || labels?.perGame == null
      ? null
      : `${labels.perGame.shortName} ${d.usage.perGame.toFixed(1)}`,
  ].filter((x): x is string => x !== null);
  return parts.length === 0 ? null : parts.join(' · ');
}

/** The card's section: each number on its own line, said in full. */
export function DeeperSection({ deeper }: { deeper: DeeperNumbers }) {
  const labels = useUsageLabels();
  if (deeper.form === null && deeper.range === null && deeper.usage === null) return null;
  return (
    <Stack gap={1}>
      <Caption>Deeper numbers</Caption>
      {deeper.usage !== null && labels !== null ? (
        <Text block>
          <span className="fsp-num">{shareText(deeper.usage.share)}</span>{' '}
          <span className="fsp-sub fsp-ui-wrap-text">{shareTail(deeper.usage, labels)}</span>
        </Text>
      ) : null}
      {deeper.usage !== null && deeper.usage.perGame !== null && labels?.perGame ? (
        <Text block>
          <span className="fsp-num">{deeper.usage.perGame.toFixed(1)}</span>{' '}
          <span className="fsp-sub fsp-ui-wrap-text">
            {labels.perGame.name.toLowerCase()} a game
          </span>
        </Text>
      ) : null}
      {deeper.form !== null ? (
        <Text block>
          <span className="fsp-num">{formText(deeper.form)}</span>{' '}
          <span className="fsp-sub fsp-ui-wrap-text">
            form: the last three weeks against the season, a game
          </span>
        </Text>
      ) : null}
      {deeper.range !== null ? (
        <Text block>
          <span className="fsp-num">{rangeText(deeper.range)}</span>{' '}
          <span className="fsp-sub fsp-ui-wrap-text">
            lowest and highest week; {deeper.range.twiceAverage} of {deeper.range.weeks} at twice
            the average
          </span>
        </Text>
      ) : null}
    </Stack>
  );
}
